import { normalizeInteractionFinalAction } from "../types/platformMetrics";
import { getAccountTimezone, getLocalDateComponents } from "./timezoneUtils";
export const generateAllPeriodsInRange = (startDate, endDate, chartDataRange) => {
    const periods = [];
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const startDay = startDate.getDate();
    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth();
    const endDay = endDate.getDate();
    const localStartDate = new Date(startYear, startMonth, startDay);
    const localEndDate = new Date(endYear, endMonth, endDay);
    const workingDate = new Date(localStartDate.getTime());
    while (workingDate <= localEndDate) {
        let periodKey = "";
        switch (chartDataRange) {
            case "Daily":
                periodKey = `${workingDate.getMonth() + 1}/${workingDate.getDate()}`;
                break;
            case "Weekly":
                const weekStart = new Date(workingDate);
                weekStart.setDate(workingDate.getDate() - workingDate.getDay());
                periodKey = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
                break;
            case "Monthly":
                periodKey = `${workingDate.getFullYear()}-${String(workingDate.getMonth() + 1).padStart(2, "0")}`;
                break;
        }
        if (!periods.includes(periodKey)) {
            periods.push(periodKey);
        }
        switch (chartDataRange) {
            case "Daily":
                workingDate.setDate(workingDate.getDate() + 1);
                break;
            case "Weekly":
                workingDate.setDate(workingDate.getDate() + 7);
                break;
            case "Monthly":
                workingDate.setDate(1);
                workingDate.setMonth(workingDate.getMonth() + 1);
                break;
        }
    }
    return periods;
};
export const initializeChartData = (periods) => {
    const data = {};
    periods.forEach((periodKey) => {
        data[periodKey] = {
            period: periodKey,
            AgentText: 0,
            BlindTransfer: 0,
            Cancel: 0,
            ControlledTransfer: 0,
            Disconnect: 0,
            Email: 0,
            Error: 0,
            HandoffTransfer: 0,
            Hangup: 0,
            VoiceMessage: 0,
        };
    });
    return data;
};
export const getInteractionPeriodKey = (interaction, chartDataRange) => {
    var _a;
    const dateString = interaction.InteractionStartDate || interaction.InteractionStartDateUtc;
    if (!dateString)
        return null;
    let localDate;
    if (interaction.InteractionStartDate) {
        const parts = (_a = dateString.split("T")[0]) === null || _a === void 0 ? void 0 : _a.split("-");
        if (!parts || parts.length !== 3)
            return null;
        const [year, month, day] = parts.map(Number);
        if (!year || !month || !day)
            return null;
        localDate = new Date(year, month - 1, day);
    }
    else {
        const timezone = getAccountTimezone(interaction.ToFilterObjectAccountId);
        const components = getLocalDateComponents(interaction.InteractionStartDateUtc, timezone);
        if (!components)
            return null;
        localDate = new Date(components.year, components.month - 1, components.day);
    }
    if (isNaN(localDate.getTime()))
        return null;
    switch (chartDataRange) {
        case "Daily":
            return `${localDate.getMonth() + 1}/${localDate.getDate()}`;
        case "Weekly":
            const weekStart = new Date(localDate);
            weekStart.setDate(localDate.getDate() - localDate.getDay());
            return `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
        case "Monthly":
            return `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, "0")}`;
        default:
            return null;
    }
};
export const sortChartData = (data, chartDataRange, dateRange) => {
    return data.sort((a, b) => {
        const periodA = a.period;
        const periodB = b.period;
        if (chartDataRange === "Monthly") {
            return periodA.localeCompare(periodB);
        }
        else {
            const [monthA, dayA] = periodA.split("/").map(Number);
            const [monthB, dayB] = periodB.split("/").map(Number);
            const startDate = parseDateToLocal(dateRange.startDate);
            const endDate = parseDateToLocal(dateRange.endDate);
            const startYear = startDate.getFullYear();
            const endYear = endDate.getFullYear();
            let yearA = startYear;
            let yearB = startYear;
            if (startYear !== endYear) {
                const startMonth = startDate.getMonth() + 1;
                if (monthA < startMonth || (monthA <= 6 && startMonth > 6))
                    yearA = endYear;
                if (monthB < startMonth || (monthB <= 6 && startMonth > 6))
                    yearB = endYear;
            }
            const dateA = new Date(yearA, monthA - 1, dayA);
            const dateB = new Date(yearB, monthB - 1, dayB);
            return dateA.getTime() - dateB.getTime();
        }
    });
};
export const parseDateToLocal = (dateString) => {
    if (dateString.includes("T")) {
        const datePart = dateString.split("T")[0];
        if (datePart) {
            const [year, month, day] = datePart.split("-").map(Number);
            if (year && month && day) {
                return new Date(year, month - 1, day);
            }
        }
    }
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
        const hasExplicitTimezoneOffset = /[+-]\d{2}:?\d{2}$/.test(dateString);
        if (dateString.endsWith("Z") || hasExplicitTimezoneOffset) {
            return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
        }
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return parsed;
};
export const generateVolumeChartData = (interactions, viewSettings, dateRange) => {
    const startDate = parseDateToLocal(dateRange.startDate);
    const endDate = parseDateToLocal(dateRange.endDate);
    const allPeriods = generateAllPeriodsInRange(startDate, endDate, viewSettings.chartDataRange);
    const data = initializeChartData(allPeriods);
    interactions.forEach((interaction) => {
        const periodKey = getInteractionPeriodKey(interaction, viewSettings.chartDataRange);
        if (periodKey && data[periodKey]) {
            const normalizedAction = normalizeInteractionFinalAction(interaction.InteractionFinalAction);
            const action = normalizedAction.replace(/\s+/g, "");
            if (data[periodKey][action] !== undefined) {
                data[periodKey][action]++;
            }
        }
    });
    return sortChartData(Object.values(data), viewSettings.chartDataRange, dateRange);
};
//# sourceMappingURL=chartUtils.js.map